import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { StickyNote, X } from 'lucide-react';

import EditClaimPanel from '../components/EditClaimPanel';
import NotesModal from '../components/NotesModal';
import { useAuth } from '../contexts/AuthContext';
import { SelectOrgPrompt, useNeedsOrg } from '../components/SelectOrgPrompt';

import ColumnSearch from '../components/companies/ColumnSearch';
import ColumnsPopover from '../components/companies/ColumnsPopover';
import CompanyClaimCell from '../components/companies/CompanyClaimCell';
import {
  FREQ_TO_DAYS, ALL_COLUMNS, LOSS_TYPES, STATUS_TYPES, STATUS_KEY_TO_LABEL,
  getCellText, getEntryStatus,
} from '../components/companies/companiesHelpers';
import { normalizeStatus } from '../utils/claimHelpers';

import { API_BASE as API } from '../config';

/**
 * Per-company claim browser. Drilldown with loss-type/status tabs, configurable
 * columns, per-column search filters, and a right-side edit drawer. Owns the
 * data fetching, table state, and modal wiring.
 */
const Companies = () => {
  const location = useLocation();
  const { selectedTenants, operatorContext, activeOrgId } = useAuth();
  const needsOrg = useNeedsOrg();
  const isOperatorInContext = operatorContext?.tenantId;
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [claims, setClaims] = useState([]);
  const [allCompanyClaims, setAllCompanyClaims] = useState([]);
  const [activeLossType, setActiveLossType] = useState('al');
  const [activeStatus, setActiveStatus] = useState('new');
  const [loading, setLoading] = useState(true);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [notesClaim, setNotesClaim] = useState(null);
  const [claimFreqDays, setClaimFreqDays] = useState(0);

  // Column state
  const [columnOrder, setColumnOrder] = useState(ALL_COLUMNS.filter((c) => c.default).map((c) => c.id));
  const [dragIdx, setDragIdx] = useState(null);
  const [columnFilters, setColumnFilters] = useState({});

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchCompanies(); }, [selectedTenants, activeOrgId, operatorContext?.tenantId, operatorContext?.orgId]);

  // Reset active company when switching tenant/org so we don't display a stale company
  useEffect(() => {
    setSelectedCompany(null);
    setAllCompanyClaims([]);
  }, [activeOrgId, operatorContext?.tenantId]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const companyFromUrl = params.get('company');
    const companyFromState = location.state?.selectedCompany;
    if (companyFromState) setSelectedCompany(companyFromState);
    else if (companyFromUrl) setSelectedCompany(companyFromUrl);
    axios.get(`${API}/tenant/settings`).then((res) => {
      const freq = res.data?.claim_update_frequency;
      setClaimFreqDays(FREQ_TO_DAYS[freq] || 0);
    }).catch(() => {});
  }, [location.search, location.state]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (selectedCompany) fetchClaims(); }, [selectedCompany]);

  // Auto-open a claim from Todo/Notification navigation
  useEffect(() => {
    const openClaim = location.state?.openClaim;
    if (openClaim && allCompanyClaims.length > 0) {
      let claim = allCompanyClaims.find((c) => c.report_no === openClaim);
      if (!claim) {
        const dbId = openClaim.split('/').pop();
        if (dbId) claim = allCompanyClaims.find((c) => c.report_no?.endsWith('/' + dbId));
      }
      if (claim) {
        if (location.state?.openNotes) {
          setNotesClaim(claim);
        } else {
          setSelectedClaim(claim);
          setIsEditOpen(true);
        }
        window.history.replaceState({}, '');
      }
    }
  }, [allCompanyClaims, location.state]);

  // Filter claims for the active loss type + status combo
  useEffect(() => {
    const targetStatus = STATUS_KEY_TO_LABEL[activeStatus];
    const filtered = allCompanyClaims.filter((c) => {
      if (c[activeLossType] !== true && c[activeLossType] !== 'true') return false;
      const typeStatus = normalizeStatus(c.type_data?.[activeLossType]?.status || c.status || 'New');
      return typeStatus === targetStatus;
    });
    setClaims(filtered);
  }, [allCompanyClaims, activeLossType, activeStatus]);

  const fetchCompanies = async () => {
    try {
      const res = await axios.get(`${API}/companies`);
      setCompanies(res.data);
      if (res.data.length > 0 && !selectedCompany && !location.state?.selectedCompany) {
        setSelectedCompany(res.data[0].name);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClaims = async () => {
    try {
      const response = await axios.get(`${API}/claims`, { params: { company_name: selectedCompany } });
      setAllCompanyClaims(response.data);
    } catch (error) {
      console.error('Error fetching claims:', error);
    }
  };

  // ── Stats ───────────────────────────────────────────────────────────────
  const globalStats = useMemo(() => ({
    newCount: allCompanyClaims.filter((c) => getEntryStatus(c) === 'New').length,
    inProcessCount: allCompanyClaims.filter((c) => getEntryStatus(c) === 'In Process').length,
    closedCount: allCompanyClaims.filter((c) => getEntryStatus(c) === 'Closed').length,
  }), [allCompanyClaims]);

  const currentStats = useMemo(() => {
    const typeClaims = allCompanyClaims.filter((c) => c[activeLossType] === true || c[activeLossType] === 'true');
    return {
      newCount: typeClaims.filter((c) => (c.type_data?.[activeLossType]?.status || c.status || 'New') === 'New').length,
      inProcessCount: typeClaims.filter((c) => (c.type_data?.[activeLossType]?.status || c.status || 'New') === 'In Process').length,
      closedCount: typeClaims.filter((c) => (c.type_data?.[activeLossType]?.status || c.status || 'New') === 'Closed').length,
    };
  }, [allCompanyClaims, activeLossType]);

  const currentLossTypeLabel = LOSS_TYPES.find((lt) => lt.key === activeLossType)?.label || '';

  // ── Row interactions ────────────────────────────────────────────────────
  const handleRowClick = (claim) => {
    setSelectedClaim(claim);
    setIsEditOpen(true);
  };

  const handleSaved = (updatedClaim) => {
    if (updatedClaim?.company_name && updatedClaim.company_name !== selectedCompany) {
      setSelectedCompany(updatedClaim.company_name);
    } else if (updatedClaim) {
      // Optimistic merge — avoids Atlas read-after-write lag
      setAllCompanyClaims((prev) => {
        const oldRn = updatedClaim.old_report_no;
        const newRn = updatedClaim.report_no;
        const newDbId = newRn?.split('/').pop();
        const idx = prev.findIndex(
          (c) => c.report_no === newRn ||
            (oldRn && c.report_no === oldRn) ||
            (newDbId && c.report_no?.endsWith('/' + newDbId)),
        );
        if (idx === -1) return [updatedClaim, ...prev];
        const next = [...prev];
        next[idx] = { ...next[idx], ...updatedClaim };
        return next;
      });
    } else if (selectedCompany) {
      fetchClaims();
    }
    if (updatedClaim) setSelectedClaim(updatedClaim);
    window.dispatchEvent(new Event('todo-refresh'));
  };

  const handleClaimRemoved = (reportNo, deleteResponse) => {
    setAllCompanyClaims((prev) => {
      if (deleteResponse?.partial && deleteResponse.report_no) {
        return prev.map((c) => {
          if (c.report_no !== reportNo) return c;
          const removed = deleteResponse.removed_loss_type;
          const newTd = { ...(c.type_data || {}) };
          if (removed) delete newTd[removed];
          return {
            ...c,
            report_no: deleteResponse.report_no,
            [removed]: false,
            type_data: newTd,
          };
        });
      }
      return prev.filter((c) => c.report_no !== reportNo);
    });
  };

  // ── Column helpers ──────────────────────────────────────────────────────
  const toggleColumn = (colId) => {
    setColumnOrder((prev) => (prev.includes(colId) ? prev.filter((id) => id !== colId) : [...prev, colId]));
  };
  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    setColumnOrder((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(idx, 0, moved);
      return next;
    });
    setDragIdx(idx);
  };
  const handleDragEnd = () => setDragIdx(null);

  const handleColumnFilterChange = useCallback((colId, value) => {
    setColumnFilters((prev) => ({ ...prev, [colId]: value }));
  }, []);

  const filteredClaims = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(([, v]) => v.trim());
    if (activeFilters.length === 0) return claims;
    return claims.filter((claim) =>
      activeFilters.every(([colId, query]) =>
        getCellText(claim, colId, activeLossType).toLowerCase().includes(query.toLowerCase()),
      ),
    );
  }, [claims, columnFilters, activeLossType]);

  const hasActiveFilters = Object.values(columnFilters).some((v) => v.trim());

  // ── Render ──────────────────────────────────────────────────────────────
  if (loading) return <div className="min-h-screen bg-gray-50 p-8 text-center">Loading...</div>;
  if (needsOrg && !isOperatorInContext) return <SelectOrgPrompt />;

  return (
    <div className="h-[calc(100vh-57px)] bg-gray-50 p-4 md:p-8 flex flex-col overflow-hidden">
      <div className="max-w-7xl mx-auto w-full flex flex-col flex-1 min-h-0">
        {/* Header */}
        <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <h1 className="text-xl md:text-2xl font-bold text-gray-800" data-testid="company-name-display">
            {selectedCompany || 'Select a Company'}
          </h1>
          <div className="text-sm text-gray-600 flex flex-wrap gap-x-2" data-testid="company-stats">
            <span className="font-semibold">Entries:</span>
            <span className="text-blue-600">New: {globalStats.newCount}</span>
            <span className="text-orange-600">In Process: {globalStats.inProcessCount}</span>
            <span className="text-green-600">Closed: {globalStats.closedCount}</span>
          </div>
        </div>

        {/* Loss Type Tabs */}
        <div className="mb-3 md:mb-4 flex gap-2 flex-wrap overflow-x-auto">
          {LOSS_TYPES.map((type) => (
            <button
              key={type.key}
              onClick={() => setActiveLossType(type.key)}
              className={`px-4 py-2 rounded text-sm transition-colors ${
                activeLossType === type.key
                  ? 'bg-gray-300 text-gray-900 font-medium'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {type.label}
            </button>
          ))}
        </div>

        {/* Status tabs + Columns popover */}
        <div className="mb-4 md:mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div className="flex gap-2">
            {STATUS_TYPES.map((status) => (
              <button
                key={status.key}
                onClick={() => setActiveStatus(status.key)}
                className={`px-4 py-2 rounded text-sm transition-colors ${
                  activeStatus === status.key
                    ? 'bg-gray-300 text-gray-900 font-medium'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {status.label}
              </button>
            ))}
          </div>
          <ColumnsPopover
            columnOrder={columnOrder}
            setColumnOrder={setColumnOrder}
            dragIdx={dragIdx}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onToggleColumn={toggleColumn}
          />
        </div>

        {/* Claims Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-auto flex-1 min-h-0">
          <Table className="table-auto">
            <TableHeader className="sticky top-0 z-10 bg-gray-50">
              <TableRow className="bg-gray-50">
                {columnOrder.map((colId) => {
                  const col = ALL_COLUMNS.find((c) => c.id === colId);
                  return col ? (
                    <TableHead key={colId} className="bg-gray-50 whitespace-nowrap">
                      <ColumnSearch
                        colId={colId}
                        label={col.label}
                        value={columnFilters[colId]}
                        onChange={handleColumnFilterChange}
                      />
                    </TableHead>
                  ) : null;
                })}
                <TableHead className="bg-gray-50">
                  {hasActiveFilters && (
                    <button
                      onClick={() => setColumnFilters({})}
                      className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-500 transition-colors whitespace-nowrap"
                      data-testid="clear-all-filters"
                    >
                      Clear Filters <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredClaims.length > 0 ? (
                filteredClaims.map((claim, index) => (
                  <TableRow
                    key={claim.report_no || index}
                    className="hover:bg-blue-50 cursor-pointer transition-colors"
                    onClick={() => handleRowClick(claim)}
                    data-testid={`company-claim-row-${index}`}
                  >
                    {columnOrder.map((colId) => (
                      <TableCell key={colId} className="whitespace-nowrap">
                        <CompanyClaimCell
                          claim={claim}
                          colId={colId}
                          activeLossType={activeLossType}
                          claimFreqDays={claimFreqDays}
                        />
                      </TableCell>
                    ))}
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8"
                        onClick={(e) => { e.stopPropagation(); setNotesClaim(claim); }}
                        data-testid={`notes-btn-${claim.report_no}`}
                      >
                        <StickyNote className="w-4 h-4 mr-1" />
                        Notes
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columnOrder.length + 2} className="text-center py-8 text-gray-500">
                    No claims found for this filter.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Loss type stats footer */}
        <div className="mt-3 md:mt-4 text-sm text-gray-600 flex flex-col sm:flex-row justify-between gap-1" data-testid="loss-type-stats">
          <span className="flex flex-wrap gap-x-2">
            <span className="font-semibold">{currentLossTypeLabel}:</span>
            <span className="text-blue-600">New: {currentStats.newCount}</span>
            <span className="text-orange-600">In Process: {currentStats.inProcessCount}</span>
            <span className="text-green-600">Closed: {currentStats.closedCount}</span>
          </span>
          <span className="text-gray-400">
            {filteredClaims.length}{hasActiveFilters ? ` of ${claims.length}` : ''} claim(s) shown
          </span>
        </div>
      </div>

      <EditClaimPanel
        claim={selectedClaim}
        open={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        companies={companies}
        onSaved={handleSaved}
        onDeleted={handleClaimRemoved}
        lossType={activeLossType}
        onSwitchType={(tk) => setActiveLossType(tk)}
      />

      <NotesModal
        open={!!notesClaim}
        onClose={() => setNotesClaim(null)}
        claim={notesClaim}
        lossType={activeLossType}
        onSwitchType={(tk) => setActiveLossType(tk)}
        onNoteAdded={() => {
          if (selectedCompany) fetchClaims();
          window.dispatchEvent(new Event('todo-refresh'));
        }}
        onFullInfo={() => {
          if (notesClaim) {
            setSelectedClaim(notesClaim);
            setIsEditOpen(true);
          }
          setNotesClaim(null);
        }}
      />
    </div>
  );
};

export default Companies;
